import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, TrendingUp, TrendingDown, Euro, PieChart as PieChartIcon, Home, FileText, Download, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getLocationLabel } from '../lib/location-utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLANS_WITH_PDF = ['starter', 'professional', 'enterprise'];

export default function Results() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader, user } = useAuth();
  const [project, setProject] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjectAndResults();
  }, [projectId]);

  const fetchProjectAndResults = async () => {
    try {
      const headers = getAuthHeader();
      const [projectRes, resultsRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`, { headers }),
        axios.post(`${API}/calculate/${projectId}`, {}, { headers })
      ]);
      
      setProject(projectRes.data);
      setResults(resultsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-warm-sand flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#005F73] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg text-stone-600">Calcul en cours...</p>
        </div>
      </div>
    );
  }

  if (!project || !results) {
    return (
      <div className="min-h-screen bg-warm-sand flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-stone-600 mb-4">Projet non trouvé</p>
          <Button onClick={() => navigate('/')}>Retour à l'accueil</Button>
        </div>
      </div>
    );
  }

  const isActiveTrial = false;
  const canExportPDF = user && PLANS_WITH_PDF.includes(user.plan);

  const fmt = (num) => {
    const n = typeof num === 'number' ? num : parseFloat(num) || 0;
    const parts = n.toFixed(2).split('.');
    const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    const dec = parts[1] === '00' ? '' : ',' + parts[1];
    return intPart + dec;
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    const primary = [0, 95, 115];
    const accent = [238, 155, 0];

    // Header
    doc.setFillColor(...primary);
    doc.rect(0, 0, 210, 35, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.text('GwadImmo Invest', 14, 18);
    doc.setFontSize(10);
    doc.text('Rapport de rentabilité immobilière', 14, 28);

    // Project info
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(16);
    doc.text(project.name, 14, 48);
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`${project.property_type} - ${getLocationLabel(project.location, project.department)}`, 14, 55);
    doc.text(`Date: ${new Date().toLocaleDateString('fr-FR')}`, 14, 61);

    // Key metrics
    doc.setFontSize(13);
    doc.setTextColor(...primary);
    doc.text('Indicateurs cles', 14, 75);

    autoTable(doc, {
      startY: 80,
      head: [['Indicateur', 'Valeur']],
      body: [
        ['Rentabilité brute', `${results.gross_yield} %`],
        ['Rentabilité nette', `${results.net_yield} %`],
        ['Cash-flow mensuel', `${fmt(results.monthly_cashflow)} EUR`],
        ['Cash-flow annuel', `${fmt(results.annual_cashflow)} EUR`],
        ['ROI', `${results.roi} %`],
      ],
      headStyles: { fillColor: primary, fontSize: 10 },
      bodyStyles: { fontSize: 10 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
    });

    // Investment details
    let y = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(13);
    doc.setTextColor(...primary);
    doc.text("Details de l'investissement", 14, y);

    autoTable(doc, {
      startY: y + 5,
      head: [['Element', 'Montant']],
      body: [
        ["Prix d'achat", `${fmt(project.purchase_price)} EUR`],
        ['Frais de notaire', `${fmt(project.notary_fees)} EUR`],
        ['Travaux', `${fmt(project.renovation_cost)} EUR`],
        ['Investissement total', `${fmt(results.total_investment)} EUR`],
      ],
      headStyles: { fillColor: accent, fontSize: 10 },
      bodyStyles: { fontSize: 10 },
    });

    // Income & charges
    y = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(13);
    doc.setTextColor(...primary);
    doc.text('Revenus et charges annuels', 14, y);

    const incomeBody = [
      ['Revenus locatifs', `+${fmt(results.annual_income)} EUR`],
      ['Charges totales', `-${fmt(results.annual_charges)} EUR`],
      ['Mensualités prêt', `-${fmt(results.monthly_loan_payment * 12)} EUR`],
    ];
    if (results.tax_reduction > 0) {
      incomeBody.push(['Reduction fiscale', `+${fmt(results.tax_reduction)} EUR`]);
    }
    incomeBody.push(['Cash-flow annuel', `${fmt(results.annual_cashflow)} EUR`]);

    autoTable(doc, {
      startY: y + 5,
      head: [['Element', 'Montant']],
      body: incomeBody,
      headStyles: { fillColor: primary, fontSize: 10 },
      bodyStyles: { fontSize: 10 },
    });

    // Financing details
    y = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(13);
    doc.setTextColor(...primary);
    doc.text('Financement', 14, y);

    autoTable(doc, {
      startY: y + 5,
      head: [['Element', 'Valeur']],
      body: [
        ['Apport personnel', `${fmt(project.financing.down_payment)} EUR`],
        ['Montant emprunte', `${fmt(project.financing.loan_amount)} EUR`],
        ["Taux d'intérêt", `${project.financing.interest_rate} %`],
        ['Durée du prêt', `${project.financing.loan_duration_years} ans`],
        ['Mensualité', `${fmt(results.monthly_loan_payment)} EUR`],
      ],
      headStyles: { fillColor: accent, fontSize: 10 },
      bodyStyles: { fontSize: 10 },
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text('GwadImmo Invest - gwadimmo-invest.com', 14, 287);
      doc.text(`Page ${i}/${pageCount}`, 190, 287, { align: 'right' });
    }

    doc.save(`${project.name.replace(/\s+/g, '_')}_rapport.pdf`);
  };

  const chargesData = [
    { name: 'Taxe foncière', value: project.charges.property_tax, color: '#005F73' },
    { name: 'Copropriété', value: project.charges.condo_fees * 12, color: '#EE9B00' },
    { name: 'Assurance', value: project.charges.insurance * 12, color: '#94D2BD' },
    { name: 'Gestion', value: project.charges.management_fees * 12, color: '#0A9396' },
    { name: 'Entretien', value: project.charges.maintenance * 12, color: '#AE2012' }
  ];

  const cashflowData = [
    { month: 'Mois 1-3', cashflow: results.monthly_cashflow },
    { month: 'Mois 4-6', cashflow: results.monthly_cashflow },
    { month: 'Mois 7-9', cashflow: results.monthly_cashflow },
    { month: 'Mois 10-12', cashflow: results.monthly_cashflow }
  ];

  return (
    <div className="min-h-screen bg-warm-sand py-12">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          <Button
            data-testid="back-btn"
            variant="ghost"
            onClick={() => navigate('/projects')}
            className="mb-4 text-[#005F73] hover:text-[#005F73]/80"
          >
            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Retour aux projets
          </Button>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-semibold text-[#005F73] mb-2 tracking-tight" data-testid="project-name">
                {project.name}
              </h1>
              <p className="text-base sm:text-lg text-stone-600">{project.property_type} • {getLocationLabel(project.location, project.department)}</p>
            </div>
            {canExportPDF ? (
              <Button
                data-testid="btn-export-pdf"
                onClick={exportPDF}
                className="rounded-full px-6 py-5 bg-[#EE9B00] hover:bg-[#EE9B00]/90 text-white shadow-lg w-full sm:w-auto"
              >
                <Download className="w-5 h-5 mr-2" strokeWidth={1.5} />
                Exporter PDF
              </Button>
            ) : (
              <Button
                data-testid="btn-export-pdf-locked"
                onClick={() => navigate('/pricing')}
                variant="outline"
                className="rounded-full px-6 py-5 border-2 border-stone-300 text-stone-400 hover:border-[#EE9B00] hover:text-[#EE9B00] w-full sm:w-auto"
              >
                <Lock className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Export PDF (Starter+)
              </Button>
            )}
          </div>
        </div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div 
            data-testid="metric-card-gross-yield"
            className="bg-white rounded-2xl border border-stone-100 p-6 shadow-[0_4px_20px_-4px_rgba(0,95,115,0.1)]"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-stone-600 font-medium">Rentabilité brute</span>
              <TrendingUp className="w-5 h-5 text-[#0A9396]" strokeWidth={1.5} />
            </div>
            <div className="text-3xl font-semibold text-[#005F73]" data-testid="gross-yield-value">
              {results.gross_yield}%
            </div>
          </div>

          <div 
            data-testid="metric-card-net-yield"
            className="bg-white rounded-2xl border border-stone-100 p-6 shadow-[0_4px_20px_-4px_rgba(0,95,115,0.1)]"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-stone-600 font-medium">Rentabilité nette</span>
              <TrendingUp className="w-5 h-5 text-[#EE9B00]" strokeWidth={1.5} />
            </div>
            <div className="text-3xl font-semibold text-[#005F73]" data-testid="net-yield-value">
              {results.net_yield}%
            </div>
          </div>

          <div 
            data-testid="metric-card-monthly-cashflow"
            className="bg-white rounded-2xl border border-stone-100 p-6 shadow-[0_4px_20px_-4px_rgba(0,95,115,0.1)]"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-stone-600 font-medium">Cash-flow mensuel</span>
              {results.monthly_cashflow >= 0 ? (
                <TrendingUp className="w-5 h-5 text-[#0A9396]" strokeWidth={1.5} />
              ) : (
                <TrendingDown className="w-5 h-5 text-[#AE2012]" strokeWidth={1.5} />
              )}
            </div>
            <div 
              className={`text-3xl font-semibold ${results.monthly_cashflow >= 0 ? 'text-[#0A9396]' : 'text-[#AE2012]'}`}
              data-testid="monthly-cashflow-value"
            >
              {results.monthly_cashflow > 0 ? '+' : ''}{results.monthly_cashflow}€
            </div>
          </div>

          <div 
            data-testid="metric-card-roi"
            className="bg-white rounded-2xl border border-stone-100 p-6 shadow-[0_4px_20px_-4px_rgba(0,95,115,0.1)]"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-stone-600 font-medium">ROI</span>
              <Euro className="w-5 h-5 text-[#94D2BD]" strokeWidth={1.5} />
            </div>
            <div className="text-3xl font-semibold text-[#005F73]" data-testid="roi-value">
              {results.roi}%
            </div>
          </div>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Investment Details */}
          <div 
            data-testid="card-investment-details"
            className="bg-white rounded-2xl border border-stone-100 p-8 shadow-[0_4px_20px_-4px_rgba(0,95,115,0.1)]"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[#005F73]/10 flex items-center justify-center">
                <Home className="w-5 h-5 text-[#005F73]" strokeWidth={1.5} />
              </div>
              <h2 className="text-2xl font-semibold text-[#005F73]">Détails de l'investissement</h2>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between items-center pb-3 border-b border-stone-200">
                <span className="text-stone-600">Prix d'achat</span>
                <span className="font-medium text-[#005F73]">{project.purchase_price.toLocaleString()}€</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-stone-200">
                <span className="text-stone-600">Frais de notaire</span>
                <span className="font-medium text-[#005F73]">{project.notary_fees.toLocaleString()}€</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-stone-200">
                <span className="text-stone-600">Travaux</span>
                <span className="font-medium text-[#005F73]">{project.renovation_cost.toLocaleString()}€</span>
              </div>
              <div className="flex justify-between items-center pt-2">
                <span className="text-stone-900 font-semibold text-lg">Investissement total</span>
                <span className="font-semibold text-[#005F73] text-xl">{results.total_investment.toLocaleString()}€</span>
              </div>
            </div>
          </div>

          {/* Income & Charges */}
          <div 
            data-testid="card-income-charges"
            className="bg-white rounded-2xl border border-stone-100 p-8 shadow-[0_4px_20px_-4px_rgba(0,95,115,0.1)]"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[#0A9396]/10 flex items-center justify-center">
                <FileText className="w-5 h-5 text-[#0A9396]" strokeWidth={1.5} />
              </div>
              <h2 className="text-2xl font-semibold text-[#005F73]">Revenus et charges annuels</h2>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between items-center pb-3 border-b border-stone-200">
                <span className="text-stone-600">Revenus locatifs</span>
                <span className="font-medium text-[#0A9396]">+{results.annual_income.toLocaleString()}€</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-stone-200">
                <span className="text-stone-600">Charges totales</span>
                <span className="font-medium text-[#AE2012]">-{results.annual_charges.toLocaleString()}€</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-stone-200">
                <span className="text-stone-600">Mensualités prêt</span>
                <span className="font-medium text-[#AE2012]">-{(results.monthly_loan_payment * 12).toLocaleString()}€</span>
              </div>
              {results.tax_reduction > 0 && (
                <div className="flex justify-between items-center pb-3 border-b border-stone-200">
                  <span className="text-stone-600">Réduction fiscale</span>
                  <span className="font-medium text-[#0A9396]">+{results.tax_reduction.toLocaleString()}€</span>
                </div>
              )}
              <div className="flex justify-between items-center pt-2">
                <span className="text-stone-900 font-semibold text-lg">Cash-flow annuel</span>
                <span className={`font-semibold text-xl ${results.annual_cashflow >= 0 ? 'text-[#0A9396]' : 'text-[#AE2012]'}`}>
                  {results.annual_cashflow > 0 ? '+' : ''}{results.annual_cashflow.toLocaleString()}€
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Charges Breakdown */}
          <div 
            data-testid="card-charges-chart"
            className="bg-white rounded-2xl border border-stone-100 p-8 shadow-[0_4px_20px_-4px_rgba(0,95,115,0.1)]"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[#EE9B00]/10 flex items-center justify-center">
                <PieChartIcon className="w-5 h-5 text-[#EE9B00]" strokeWidth={1.5} />
              </div>
              <h2 className="text-2xl font-semibold text-[#005F73]">Répartition des charges annuelles</h2>
            </div>

            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={chargesData}
                  cx="50%"
                  cy="45%"
                  labelLine={false}
                  label={false}
                  outerRadius={90}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {chargesData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `${value}€`} />
                <Legend 
                  verticalAlign="bottom" 
                  height={80}
                  formatter={(value, entry) => `${entry.payload.name}: ${entry.payload.value}€`}
                  wrapperStyle={{ paddingTop: '20px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Cashflow Evolution */}
          <div 
            data-testid="card-cashflow-chart"
            className="bg-white rounded-2xl border border-stone-100 p-8 shadow-[0_4px_20px_-4px_rgba(0,95,115,0.1)]"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[#94D2BD]/20 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-[#0A9396]" strokeWidth={1.5} />
              </div>
              <h2 className="text-2xl font-semibold text-[#005F73]">Cash-flow mensuel estimé</h2>
            </div>

            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={cashflowData}>
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="cashflow" fill={results.monthly_cashflow >= 0 ? '#0A9396' : '#AE2012'} name="Cash-flow (€)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Button
            data-testid="btn-view-all-projects"
            onClick={() => navigate('/projects')}
            className="rounded-full px-8 py-6 text-lg bg-[#005F73] hover:bg-[#005F73]/90"
          >
            Voir tous mes projets
          </Button>
          <Button
            data-testid="btn-new-project"
            onClick={() => navigate('/calculator')}
            variant="outline"
            className="rounded-full px-8 py-6 text-lg border-2 border-[#005F73] text-[#005F73] hover:bg-[#005F73]/5"
          >
            Nouveau projet
          </Button>
        </div>
      </div>
    </div>
  );
}
